import React, { useState, useEffect } from 'react';
import { db } from '../../mockDB';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { VoucherManagement } from './VoucherManagement';

const TAB_USERS = 'users';
const TAB_VOUCHERS = 'vouchers';

export const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(TAB_USERS);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    const data = await db.getAllUsers();
    setUsers(data);
    setLoading(false);
  };

  if (loading) return <div className="p-8 text-center">Loading system data...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">System Administration</h1>
        <p className="page-subtitle">Manage users, roles, and platform settings</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
        {[
          { label: 'Total Users', value: users.length, icon: FiIcons.FiUsers, color: 'bg-blue-50 text-blue-600' },
          { label: 'Active Clients', value: users.filter((u) => u.role === 'client' && u.subscription_status === 'active').length, icon: FiIcons.FiActivity, color: 'bg-emerald-50 text-emerald-600' },
          { label: 'Advisors', value: users.filter((u) => u.role === 'advisor').length, icon: FiIcons.FiBriefcase, color: 'bg-amber-50 text-amber-600' },
        ].map((stat) => (
          <div key={stat.label} className="card shadow-sm">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-slate-500 font-medium mb-1">{stat.label}</p>
                <p className="text-3xl font-bold text-brand-navy">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <SafeIcon icon={stat.icon} className="text-xl" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-full sm:w-fit overflow-x-auto">
        {[
          { id: TAB_USERS, label: 'User Directory', icon: FiIcons.FiUsers },
          { id: TAB_VOUCHERS, label: 'Voucher Management', icon: FiIcons.FiTag },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id ? 'bg-white text-brand-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <SafeIcon icon={tab.icon} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === TAB_USERS && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <h2 className="font-bold text-brand-navy">User Directory</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="px-4 md:px-6 py-3 font-medium">Name</th>
                  <th className="px-4 md:px-6 py-3 font-medium">Email</th>
                  <th className="px-4 md:px-6 py-3 font-medium">Role</th>
                  <th className="px-4 md:px-6 py-3 font-medium">Status</th>
                  <th className="px-4 md:px-6 py-3 font-medium">Voucher</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50">
                    <td className="px-4 md:px-6 py-4 font-medium text-brand-navy whitespace-nowrap">{user.name}</td>
                    <td className="px-4 md:px-6 py-4 text-slate-500 whitespace-nowrap">{user.email}</td>
                    <td className="px-4 md:px-6 py-4 capitalize">
                      <span className={`status-badge ${
                        user.role === 'admin' ? 'bg-purple-100 text-purple-700 border-purple-200'
                          : user.role === 'advisor' ? 'bg-amber-100 text-amber-700 border-amber-200'
                          : 'bg-slate-100 text-slate-700 border-slate-200'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      {user.role === 'client' ? (
                        <span className={`status-badge ${
                          user.subscription_status === 'active'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-red-50 text-red-700 border-red-200'
                        }`}>
                          {user.subscription_status}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 md:px-6 py-4">
                      {user.applied_voucher ? (
                        <code className="bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded text-xs font-bold tracking-wider">
                          {user.applied_voucher}
                        </code>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === TAB_VOUCHERS && <VoucherManagement />}
    </div>
  );
};